import { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import { IconPrefix } from '@components/primitives/Icon/IconPrefix'
import Link from '@components/primitives/Link'
import { SelectBox } from '@components/primitives/SelectBox/SelectBox.client'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faMicroscope } from '@fortawesome/pro-regular-svg-icons/faMicroscope'
import { faStamp } from '@fortawesome/pro-regular-svg-icons/faStamp'
import { faStethoscope } from '@fortawesome/pro-regular-svg-icons/faStethoscope'
import { faSyringe } from '@fortawesome/pro-regular-svg-icons/faSyringe'
import type { TreatmentPageObject } from '@modules/drugs/helpers/getTreatmentPageData'
import { useState } from 'react'

export const TreatmentPageHero = (props: NonNullable<TreatmentPageObject>) => {
  const conditions = props.drugConditions
  return (
    <div className={`bg-linearDarkGarden-grainy rounded-2xl`}>
      <div className='flex rounded-2xl p-8'>
        <div className='w-full md:w-2/3'>
          <TreatmentHeroText {...props} conditions={conditions} />
        </div>
        <div className='hidden md:block md:w-1/3'>
          {conditions.length > 0 && (
            <TreatmentCard {...props} conditions={conditions} />
          )}
        </div>
      </div>
    </div>
  )
}

const TreatmentHeroText = (
  props: NonNullable<TreatmentPageObject> & { conditions: string[] },
) => {
  const fdaApprovals = props.drugApprovedUses
  const MAX_CONDITIONS_TO_SHOW = 3
  const conditionsString = `${props.conditions
    .slice(0, MAX_CONDITIONS_TO_SHOW)
    .join(', ')} ${
    props.conditions.length > MAX_CONDITIONS_TO_SHOW
      ? `+ ${props.conditions.length - MAX_CONDITIONS_TO_SHOW} more`
      : ''
  }`
  return (
    <>
      <Text
        className='mb-4 text-white'
        element='h1'
        styleName='h1'
        value={props.searchName}
      />
      <div className='w-full md:w-2/3'>
        {props.conditions.length > 0 && (
          <div className='flex items-baseline'>
            <Icon className='mr-4 text-white' icon={faStethoscope} />
            <Text
              className='text-white'
              styleName='p'
              value={conditionsString}
            />
          </div>
        )}
        <div className='flex items-baseline'>
          {/* TODO - maybe this is an enum and we can have different icons for different types */}
          <Icon className='mr-4 text-white' icon={faSyringe} />
          <Text
            className='text-white'
            styleName='p'
            // Either "SmallMoleculeDrug" or "BiotechDrug"
            value={'Treatment'}
          />
        </div>
        {fdaApprovals.length ? (
          <div className='flex items-baseline'>
            <Icon className='mr-4 text-white' icon={faStamp} />
            <Text
              className='text-white'
              styleName='p'
              value={`${fdaApprovals.length} FDA approval${
                fdaApprovals.length === 1 ? '' : 's'
              }`}
            />
          </div>
        ) : null}
        <div className='flex items-baseline'>
          <Icon className='mr-4 text-white' icon={faMicroscope} />
          <Text
            className='text-white'
            styleName='p'
            value={`${props.trials.length} Active Stud${
              props.trials.length === 1 ? 'y' : 'ies'
            } for ${props.searchName}`}
          />
        </div>
      </div>
    </>
  )
}

const TreatmentCard = ({ conditions }: { conditions: string[] }) => {
  const conditionOptions = conditions.map((name) => {
    return { label: name, value: name }
  })

  const [condition, setCondition] = useState<string | undefined>(
    conditionOptions[0]?.label,
  )
  return (
    <div className={'rounded-lg bg-green50 p-6'}>
      <SelectBox
        className={classMerge('mb-4 w-full', condition && '!border-success300')}
        description='Treatment for'
        icon={faStethoscope}
        iconColor={condition ? 'text-success300' : 'text-neutral500'}
        instanceId={'treatment-for-select'}
        isClearable={false}
        items={conditionOptions}
        onChange={setCondition}
        optionLabelClassName='truncate'
        placeholder='Select'
        showBorderStates
        showCheckCircle
        value={condition}
      />
      {/* TODO - ensure that we have calculated efficacy scores
      <Score.Effectiveness containerClassName='my-2' rating={1} />
      <Text
        className='text-green800'
        className='my-2'
        styleName='p-small'
        value='This is better than 65% of similar treatments'
      />
      */}
      <Link
        className={classMerge(
          buttonClassNames.legacy.fill.green,
          'w-full rounded-full',
        )}
        href='#relatedTrials'
      >
        <IconPrefix icon={faMicroscope}>Go To Trials</IconPrefix>
      </Link>
    </div>
  )
}
